import { Component, OnInit, Output, EventEmitter, Input, ViewChild } from '@angular/core';
import { FormGroup, FormBuilder } from '@angular/forms';
import { CampaignMaintenanceService } from 'src/app/pages/maintenance/campaign-maintenance/campaign-maintenance.service';
import { debounceTime, filter, map, finalize, takeUntil } from 'rxjs/operators';
import { MatAutocomplete, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { of as observableOf, Subscription, fromEvent } from 'rxjs';
import { DialogService, LanguageService } from 'src/app/services';
import { environment } from 'src/environments/environment';
import { requestCampaign } from 'src/app/model/requests';
import * as _ from 'lodash';

@Component({
  selector: 'esc-campaign-selector',
  templateUrl: './campaign-selector.component.html',
  styleUrls: ['./campaign-selector.component.scss']
})
export class CampaignSelectorComponent implements OnInit {
  @Input() textLabel;
  @Input() wrapperLayoutAlign;
  @Input() labelFlex;
  @Input() formWrapperFlex;
  @Input() campaignData;
  @Input() isDisabled;
  @Output() campaignSelected = new EventEmitter;
  @Output() errorLoading = new EventEmitter;
  @Output() loadingCampaignData = new EventEmitter;
  
  @ViewChild('campaignAC') campaignACRef: MatAutocomplete;
  @ViewChild(MatAutocompleteTrigger) campaignACTr: MatAutocompleteTrigger;

  form: FormGroup;

  loading = {
    campaign: false,
    init: false,
    more: false,
    error: false
  };
  public listData = [];
  private currentFilters;
  private listSub: Subscription;
  networkErrMsg = this.languageService.getTranslation("MSG_ERROR_NETWORK_CONNECTION", {});

  constructor(
    private fb: FormBuilder,
    private dialogService: DialogService,
    private languageService: LanguageService,
    public campMntSvc: CampaignMaintenanceService,
    ) { }

  ngOnInit() {
    this.currentFilters = _.cloneDeep(requestCampaign);
    this.initForm();
    this.detectFormChanges();
    // console.log(this.campaignData);
  }

  ngOnDestroy() {
    if (this.listSub && !this.listSub.closed) {
      this.listSub.unsubscribe();
    }
  }

  initForm() {
    this.form = this.fb.group({
      campaign: null
    })

    if(this.campaignData) {
      let campaignObj = {
        code: _.get(this.campaignData, 'campaign_code'),
        description: _.get(this.campaignData, 'campaign_description'),
      }
      this.form.get('campaign').setValue(campaignObj, { emitEvent: true });
      this.currentFilters.web_filter.keyword = _.get(this.campaignData, 'campaign_description');
      this.getListData('edit');
    }

    if(this.isDisabled) {
      this.form.disable();
    } else {
      this.form.enable();
    }
  }

  detectFormChanges() {
    this.form.get('campaign').valueChanges.pipe(
      debounceTime(600),
      filter((term) => !_.isObject(term)),
      // distinctUntilChanged(),
      map((term: string) => _.trim(term)),
    )
    .subscribe(value => {
      this.currentFilters.web_filter.keyword = _.toString(value);
      this.listData = [];
      this.getListData(null);
    });
  }

  getInitialValues(data) {
    this.currentFilters.web_filter.offset = 0;
    this.currentFilters.web_filter.keyword = "";
    this.listData = [];
    this.getListData(null);
  }
   
  getMoreData(name) {
    if (this.currentFilters.getMore) {
      this.currentFilters.web_filter.offset += requestCampaign.web_filter.limit;
      this.getListData(name);
    }
  }

  getListData(mode) {
    this.loading.campaign = true;
    if (this.loading.init == true) {
      return;
    }
    this.loading.error = false;
    if (this.listData.length == 0) {
      this.loading.init = true;
    } else {
      this.loading.more = true;
    }

    if(mode == 'edit') { 
      this.loadingCampaignData.next(true);
    }
    this.listSub = this.campMntSvc.getList(this.currentFilters).pipe(
      finalize(() => {
        this.listSub.unsubscribe();
        return observableOf(null);
      }))
      .subscribe((res: any) => {
        if (environment.showLogs) {
          console.log(res);
        }
        this.loading.campaign = false;
        this.loading.init = false;
        this.loading.more = false;
        if (res == null || res.status == 0 || res.status == 400 || res.status == 500 || res.status == 502 || res.status == 504) {
          this.loading.error = true;
          this.listData = [];
          this.dialogService.openSnackBar(this.networkErrMsg)
          if(mode == 'edit') { 
            this.errorLoading.next(true);
          }
          return;
        }
        
        if (_.has(res, 'value') && (res.value == "Success")) {
          this.listData = this.listData.concat(res.data);

          if((res.data.length > 0) && (this.listData.length < _.get(res, 'count'))) {
            this.currentFilters.getMore = true;
          }
        }

        if((_.toLower(_.get(res, 'value')) == "empty object") && (this.currentFilters.getMore == true)) {
          this.currentFilters.getMore = false;
        }

        if(mode == 'edit') {
          this.loadingCampaignData.next(false);
          this.filterListdata();
        }
      }, err => {
        if (environment.showLogs) {
          console.error(err);
        }
        if(mode == 'edit') { 
          this.errorLoading.next(true);
        }
        this.loading.campaign = false;
        if (this.listData.length == 0) {
          this.loading.error = true;
        } else {
          this.dialogService.openSnackBar(this.languageService.getTranslation("MSG_INTERNAL_SERVER_ERROR", {}));
          this.currentFilters.web_filter.limit -= this.currentFilters.web_filter.offset;
        }
        return observableOf(null);
      });
  }

  filterListdata() {
    let campaignObj = {};
    campaignObj = this.listData.filter(e => {
      return e.id == _.get(this.campaignData, 'campaign_id')
    })
    this.form.get('campaign').setValue(campaignObj[0]);
    this.autoCmpltClosed();
  }

  displayFn(data?: any): string | undefined {
    if(data) {
      return _.get(data, 'code') + ' - ' + _.get(data, 'description');
    }
    return null;
  }

  autoCmpltClosed() {
    this.listData = [];

    if (_.isObject(_.get(this.form.get('campaign'), 'value'))) {
      this.campaignSelected.next(_.get(this.form.get('campaign'), 'value'))
    }
  }

  clearForm(ev: Event) {
    this.form.get('campaign').setValue(null, { emitEvent: false });
    this.campaignSelected.next(null)
    // ev.stopPropagation();
  }

  autocompleteScroll(name, refName, acTrigger) {
    setTimeout(() => {
      if (this[refName].isOpen || this[refName].panel) {
        fromEvent(this[refName].panel.nativeElement, 'scroll')
          .pipe(
            map(x => this[refName].panel.nativeElement.scrollTop),
            takeUntil(this[acTrigger].panelClosingActions)
          )
          .subscribe(x => {
            const scrollTop = this[refName].panel.nativeElement
              .scrollTop;
            const scrollHeight = this[refName].panel.nativeElement
              .scrollHeight;
            const elementHeight = this[refName].panel.nativeElement
              .clientHeight;
            const atBottom = scrollHeight === scrollTop + elementHeight;
            if (atBottom) {
              // fetch more data
              this.getMoreData(name);
            }
          });
      }
    });
  }
}